import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Flex,
  SegmentedControl,
  Group,
  Text,
  TextInput,
  Stack,
  Image,
} from '@mantine/core';
import toast from 'react-hot-toast';

import entityIcon from '@util/entityIcon';
import { PaginatedList } from '@components/shared';
import { AssociateButton } from './ManagerAssociate';

export default function ManagerList({
  items = null,
  requestInfo = {
    url: `/retrieve-managers/`,
    data: {},
  },
  refresh,
  isIndex,
  organizationId,
  locationId,
}) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState({
    status: 'entire',
  });
  const [refresh2, setRefresh2] = useState(false);

  const isAdmin = useSelector((state) => state.admin) ? true : false;

  const reqData = {
    ...filters,
    ...requestInfo.data,
    ...filters,
    status: filters.status === 'entire' ? [1, 2, 3, 4] : filters.status,
    search_value: searchValue,
    refresh,
    refresh2,
  };

  const showJoinStatus = organizationId || locationId ? true : false;

  return (
    <div>
      {requestInfo && (
        <React.Fragment>
          <TextInput
            placeholder="Search for a manager..."
            size="lg"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <SegmentedControl
            value={filters.status}
            data={[
              { label: t('common:all'), value: 'entire' },
              { label: t('common:active'), value: '2' },
              { label: t('common:created'), value: '1' },
              { label: t('common:disabled'), value: '4' },
            ]}
            onChange={(e) => setFilters({ ...filters, status: e })}
            my="xs"
          />
        </React.Fragment>
      )}
      <PaginatedList
        tableData={items}
        requestInfo={{
          url: requestInfo.url,
          data: reqData,
        }}
        responseMapping={(r) =>
          r.map((m) => ({
            ...m,
            link_url: `/managers/${m.id}`,
          }))
        }
        displayFormatter={(item) => {
          const matchingLocation = showJoinStatus
            ? locationId
              ? item.original.location_joins.find(
                  (f) => f.location_id === locationId
                )
              : null
            : null;

          const matchingOrg = showJoinStatus
            ? organizationId
              ? item.original.organization_joins.find(
                  (f) => f.organization_id === organizationId
                )
              : null
            : null;

          const matchingEntity = matchingLocation || matchingOrg;

          return (
            <Card key={item.original.id}>
              <Group>
                <div>
                  <Avatar
                    src={
                      item.original.profile_asset
                        ? item.original.profile_asset.filename_url
                        : null
                    }
                    size="lg"
                    radius="100%"
                  />
                </div>
                <div>
                  <Group gap="xs">
                    <Text
                      fw={700}
                      component={Link}
                      to={`/managers/${item.original.id}`}
                    >
                      {item.original.first_name} {item.original.last_name}
                    </Text>
                    {!showJoinStatus ? (
                      <>
                        {item.original.status === 1 && (
                          <Badge variant="light" color="yellow">
                            Waiting on activation
                          </Badge>
                        )}
                        {item.original.status === 2 && (
                          <Badge variant="light" color="green">
                            {t('common:active')}
                          </Badge>
                        )}
                        {item.original.status === 4 && (
                          <Badge variant="light" color="gray">
                            {t('common:disabled')}
                          </Badge>
                        )}
                      </>
                    ) : (
                      <>
                        {matchingEntity && (
                          <>
                            {item.original.status === 1 && (
                              <Badge variant="light" color="yellow">
                                Signup incomplete
                              </Badge>
                            )}
                            {item.original.status === 2 &&
                              matchingEntity.status === 2 && (
                                <Badge variant="light" color="green">
                                  {t('common:active')}
                                </Badge>
                              )}
                            {item.original.status === 2 &&
                              matchingEntity.status === 1 && (
                                <Badge variant="light" color="blue">
                                  {t('common:invited')}
                                </Badge>
                              )}
                          </>
                        )}
                      </>
                    )}
                    {item.original.logged_in && (
                      <Badge variant="light" color="green">
                        {t('common:logged_in')}
                      </Badge>
                    )}
                    {!item.original.logged_in && item.original.status !== 4 && (
                      <Badge variant="light" color="gray">
                        {t('common:logged_out')}
                      </Badge>
                    )}
                  </Group>
                  <Group gap="xs" mt={5}>
                    {entityIcon.email()}
                    <Text size="sm">{item.original.email}</Text>
                  </Group>
                  {requestInfo && requestInfo.data && !isIndex && isAdmin && (
                    <Flex align="center" mt="xs" gap="xs">
                      {matchingEntity &&
                        item.original.status === 2 &&
                        matchingEntity.status === 1 && (
                          <AssociateButton
                            buttonProps={{
                              size: 'xs',
                              fullWidth: false,
                              p: 0,
                              color: 'gray',
                              variant: 'subtle',
                            }}
                            buttonText="Resend Invite"
                            url={`/managers/${item.original.id}/add-to-entity/`}
                            onSuccess={() => {
                              setRefresh2(!refresh2);
                            }}
                            reqData={{
                              organization_id: organizationId,
                              location_id: locationId,
                              manager_id: item.original.id,
                            }}
                          />
                        )}
                      <RemoveButton
                        reqData={{
                          ...requestInfo.data,
                          manager_id: item.original.id,
                          remove: true,
                        }}
                        managerId={item.original.id}
                        onSuccess={() => setRefresh2(!refresh2)}
                      />
                    </Flex>
                  )}
                </div>
              </Group>
            </Card>
          );
        }}
      />
    </div>
  );
}

const RemoveButton = ({ reqData, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  function onClick() {
    const req = {
      ...reqData,
    };

    setLoading(true);

    axios
      .post(`/remove-manager/`, req)
      .then(() => {
        setLoading(false);
        toast.success('Removed!');
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <Button
      onClick={onClick}
      size="xs"
      variant="subtle"
      color="gray"
      loading={loading}
      leftSection={entityIcon.trash()}
      radius="lg"
      p={0}
    >
      Remove
    </Button>
  );
};
